import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PdfExporter = ({
                         url,
                         fileName = 'export.pdf',
                         buttonText = 'Export to PDF',
                         buttonProps = {},
                         companyName = '',
                         onSuccess = () => {},
                         onError = () => {}
                     }) => {
    const [isExporting, setIsExporting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Simple image fetcher that converts to base64
    const fetchImageAsBase64 = async (imageUrl) => {
        try {
            const response = await fetch(imageUrl, {
                mode: 'cors',
                cache: 'no-cache'
            });

            if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.warn(`Error fetching image: ${imageUrl}`, error);
            return null;
        }
    };

    // Helper to convert SVG to data URL for better rendering
    const processSvgElements = async (container) => {
        const svgElements = container.querySelectorAll('.tip-icon svg');
        for (const svg of svgElements) {
            try {
                const svgString = new XMLSerializer().serializeToString(svg);
                const encodedSvg = encodeURIComponent(svgString);
                const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

                const img = document.createElement('img');
                img.src = dataUrl;
                img.style.width = svg.getAttribute('width') || '6vh';
                img.style.height = svg.getAttribute('height') || '6vh';

                const iconDiv = svg.closest('.tip-icon');
                if (iconDiv) {
                    // Keep the icon div but replace contents
                    while (iconDiv.firstChild) {
                        iconDiv.removeChild(iconDiv.firstChild);
                    }
                    iconDiv.appendChild(img);
                }
            } catch (error) {
                console.warn('Error processing SVG:', error);
            }
        }
    };

    const handleExport = async () => {
        if (!url) {
            setSnackbar({
                open: true,
                message: 'No URL provided for export',
                severity: 'error'
            });
            onError(new Error('No URL provided'));
            return;
        }

        setIsExporting(true);
        setSnackbar({
            open: true,
            message: 'Preparing PDF export...',
            severity: 'info'
        });

        try {
            // Add cache-busting parameter
            const cacheBustedUrl = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
            console.log(`Fetching content from: ${cacheBustedUrl}`);

            // Fetch the HTML content
            const response = await fetch(cacheBustedUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'text/html',
                    'Cache-Control': 'no-cache',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch content: ${response.status} ${response.statusText}`);
            }

            // Get the HTML content
            let htmlContent = await response.text();

            // Extract background image URL
            let backgroundImageUrl = null;
            const bgMatch = htmlContent.match(/background-image:\s*url\(['"]?(https:\/\/[^'"]+)['"]?\)/i);
            if (bgMatch && bgMatch[1]) {
                backgroundImageUrl = bgMatch[1];
                console.log("Found background image URL:", backgroundImageUrl);
            }

            // Create a temporary container to render the content
            const container = document.createElement('div');
            container.style.width = '1500px'; // Wider container for a better layout
            container.style.margin = '0';
            container.style.padding = '0';
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '-9999px';
            container.innerHTML = htmlContent;

            // Add to DOM temporarily
            document.body.appendChild(container);

            // Fix overflow issues
            const fixOverflowElements = [
                '.wrapper', '.container', '.tips-grid',
                '.tip', 'body', '.header'
            ];

            fixOverflowElements.forEach(selector => {
                const elements = container.querySelectorAll(selector);
                elements.forEach(el => {
                    el.style.overflow = 'visible';
                });
            });

            // Process any client logo images to ensure they load
            const logoImg = container.querySelector('.header img');
            if (logoImg && logoImg.src) {
                try {
                    const base64Logo = await fetchImageAsBase64(logoImg.src);
                    if (base64Logo) {
                        logoImg.src = base64Logo;
                        console.log("Applied logo as base64");
                    }
                } catch (error) {
                    console.warn("Error processing logo:", error);
                }
            }

            // Force the background image if we found one
            if (backgroundImageUrl) {
                const tipsGrid = container.querySelector('.tips-grid');
                if (tipsGrid) {
                    // Fetch and convert background image to base64
                    const base64BgImage = await fetchImageAsBase64(backgroundImageUrl);
                    if (base64BgImage) {
                        tipsGrid.style.backgroundImage = `url(${base64BgImage})`;
                        console.log("Applied background image as base64 to tips-grid");
                    }
                }
            }

            // Fix the tips-grid to ensure proper alignment
            const tipsGrid = container.querySelector('.tips-grid');
            if (tipsGrid) {
                tipsGrid.style.display = 'grid';
                tipsGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
                tipsGrid.style.gap = '2vh';
                tipsGrid.style.maxWidth = '95%'; // Wider grid

                // Fix individual tip alignment
                const tipElements = tipsGrid.querySelectorAll('.tip');
                tipElements.forEach(tip => {
                    tip.style.boxSizing = 'border-box';
                    tip.style.height = '100%'; // Same height for all tips in a row
                });
            }

            // Add custom print styles to fix layout in PDF
            const styleElement = document.createElement('style');
            styleElement.textContent = `
                @media print {
                    .tips-grid {
                        display: grid !important;
                        grid-template-columns: repeat(3, 1fr) !important;
                        gap: 2vh !important;
                        max-width: 95% !important;
                    }
                    .tip {
                        box-sizing: border-box !important;
                        height: 100% !important;
                        break-inside: avoid !important;
                    }
                    body, html, .wrapper, .container {
                        overflow: visible !important;
                    }
                }
            `;
            container.appendChild(styleElement);

            // Process SVG elements to ensure they render correctly
            await processSvgElements(container);

            // Wait for all resources to load
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Calculate total content height
            const wrapperElement = container.querySelector('.wrapper');
            const contentHeight = wrapperElement ? wrapperElement.scrollHeight + 100 : container.scrollHeight + 100;
            container.style.height = `${contentHeight}px`;

            // Use html2canvas with optimal settings
            const canvas = await html2canvas(container, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                width: 1500,
                height: contentHeight,
                backgroundColor: '#ffffff',
                imageTimeout: 15000,
                onclone: function(clonedDoc) {
                    // Fix overflow for all elements
                    fixOverflowElements.forEach(selector => {
                        const elements = clonedDoc.querySelectorAll(selector);
                        elements.forEach(el => {
                            el.style.overflow = 'visible';
                        });
                    });

                    // Fix the grid layout in the clone
                    const tipsGrid = clonedDoc.querySelector('.tips-grid');
                    if (tipsGrid) {
                        tipsGrid.style.display = 'grid';
                        tipsGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
                        tipsGrid.style.gap = '2vh';
                        tipsGrid.style.maxWidth = '95%';

                        // Fix individual tip alignment in the clone
                        const tipElements = tipsGrid.querySelectorAll('.tip');
                        tipElements.forEach(tip => {
                            tip.style.boxSizing = 'border-box';
                            tip.style.height = '100%';
                        });
                    }
                }
            });

            // Initialize PDF
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            // Convert canvas to image
            const imgData = canvas.toDataURL('image/jpeg', 0.95);

            // Calculate dimensions
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add image to first page
            pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

            // Add additional pages if needed
            let heightLeft = imgHeight - pageHeight;
            let position = -pageHeight;

            while (heightLeft > 0) {
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
                position -= pageHeight;
            }

            // Save the PDF
            pdf.save(fileName);

            // Clean up
            document.body.removeChild(container);

            setSnackbar({
                open: true,
                message: `Successfully exported PDF as ${fileName}`,
                severity: 'success'
            });

            onSuccess(fileName);
        } catch (error) {
            console.error("PDF export error:", error);
            setSnackbar({
                open: true,
                message: `Failed to export PDF: ${error.message || "Unknown error"}`,
                severity: 'error'
            });

            onError(error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Button
                variant="contained"
                color="secondary"
                onClick={handleExport}
                disabled={isExporting || !url}
                startIcon={isExporting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                {...buttonProps}
            >
                {isExporting ? "Exporting..." : buttonText}
            </Button>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

PdfExporter.propTypes = {
    url: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    buttonText: PropTypes.string,
    buttonProps: PropTypes.object,
    companyName: PropTypes.string,
    onSuccess: PropTypes.func,
    onError: PropTypes.func
};

export default PdfExporter;